/**
 * TestsGuide: the visual guide of the tests (help id "tests"), opened by the "?" after the title of the test bank
 * (/test_list), of the create forms (/test_create, /exams_list/:uuid/create_test) and of the builder (/test/:uuid):
 * they share the seen mark, so it tells the whole way once: create a test, fill in its settings, find questions in
 * the bank, look at them and add them, arrange them, check and finish, put the test into an exam.
 * Written as BankGuide is: HelpKit blocks in their order, Mini pictures of the real controls, the sentences under
 * help.tests.* and every button named through its own i18n key.
 * It follows the screen with the pages' own breakpoints: 768px (the list's table or cards, the builder's header with
 * Preview and Edit in "⋯" below it, the bank's folder choice on a row of its own and the sorting folded under
 * Filters below it), 640px (the bank row's add button with its label, a test's row on one line) and 1280px (the bank
 * and the test side by side, two tabs below). The bank side lists approved questions only (the builder asks for them),
 * so its rows show no lock; a test can also be made at once from a subject on the Folders page (its "⋯"). Everyone
 * who opens these pages (create_exam) can do all of it, so there is no roles block.
 * Props: `passages`, the server has passages (the builder's "+ Context question"; its tip shows only then).
 * FillButton below is the one part of its own (Mini has no main button that fills its row, as the phones' do).
 */
import React from "react";
import { useTranslation } from "react-i18next";
import {
  AdjustmentsHorizontalIcon,
  ArrowDownIcon,
  ArrowUpIcon,
  ArrowUpTrayIcon,
  CheckCircleIcon,
  CheckIcon,
  ChevronDoubleDownIcon,
  ChevronDoubleUpIcon,
  ChevronRightIcon,
  DocumentPlusIcon,
  EllipsisHorizontalIcon,
  EllipsisVerticalIcon,
  ExclamationTriangleIcon,
  EyeIcon,
  EyeSlashIcon,
  ListBulletIcon,
  LockClosedIcon,
  MagnifyingGlassIcon,
  PencilSquareIcon,
  PlusIcon,
  TrashIcon,
} from "@heroicons/react/24/outline";
import { HelpFlow, HelpIntro, HelpLegend, HelpStep, HelpSteps, HelpText, HelpTips } from "../HelpKit";
import {
  Mark,
  MiniBadge,
  MiniButton,
  MiniCard,
  MiniCheck,
  MiniDialog,
  MiniDivider,
  MiniField,
  MiniIcon,
  MiniLevel,
  MiniMenu,
  MiniRow,
  MiniScreen,
  MiniStack,
  MiniStatus,
  MiniStepper,
  MiniTable,
  MiniTabs,
  MiniText,
} from "../Mini";
import { useMediaQuery } from "../../../pages/Exams/TestBuilder/ui";

// A phone's main button, filling its row ("+ Create test" on its own, Done beside "⋯"): MiniButton's "primary" look
// (the same tokens), which Mini can't stretch. A candidate for Mini.js, as a `fill` of MiniButton.
function FillButton({ icon: Icon, children, mark = false }) {
  const button = (
    <span className="flex h-6 min-w-0 flex-1 items-center justify-center gap-1 whitespace-nowrap rounded-md bg-blue-600 px-2 text-[11px] font-medium text-white shadow-sm shadow-blue-600/25 md:h-7 md:text-xs">
      {Icon && <Icon className="h-3.5 w-3.5 shrink-0" />}
      <span className="truncate">{children}</span>
    </span>
  );
  return mark ? (
    <Mark pointer block round="md">
      {button}
    </Mark>
  ) : (
    button
  );
}

export default function TestsGuide({ passages = false }) {
  const [t] = useTranslation();
  // The pages' own breakpoints (see above).
  const wide = useMediaQuery("(min-width: 768px)");
  const roomy = useMediaQuery("(min-width: 640px)");
  const split = useMediaQuery("(min-width: 1280px)");
  const ex = (key) => t(`help.common.example.${key}`);
  const own = (key) => t(`help.tests.example.${key}`);

  // The labels of the real controls.
  const create = t("test_bank.list.create_test");
  const submit = t("test_bank.single_create.submit_and_add");
  const heading = t("test_bank.form.heading");
  const calculator = t("test_bank.form.calculator_allowed");
  const table = t("test_bank.form.periodic_table_allowed");
  const edit = t("test_bank.list.edit_test");
  const filters = t("test_bank.filters.title");
  const hide = t("test_bank.builder.hide_added");
  const bankTab = t("test_bank.builder.tab_bank");
  const testTab = t("test_bank.builder.tab_test");
  const add = t("test_bank.builder.add");
  const addToTest = t("test_bank.builder.add_to_test");
  // The selection bar's button: in the picture with as many questions as it ticks, in the text with "…" (the count
  // is the reader's own).
  const addManyPicture = t("test_bank.editor.add_questions_count", { count: 2 });
  const addMany = t("test_bank.editor.add_questions_count", { count: "…" });
  const remove = t("test_bank.builder.remove_from_test");
  const defaultPoints = t("test_bank.editor.default_points");
  const saved = t("test_bank.builder.saved");
  const preview = t("test_bank.preview.open");
  const done = t("test_bank.builder.done");
  const addTest = t("exam_bank.view.add_test");
  const existing = t("exam_bank.add_test.select_existing");
  const createNew = t("exam_bank.add_test.create_new");
  // The bank's folder choice as it shows a subject: "Exam › Subject".
  const folderChoice = `${ex("exam")} › ${ex("subject")}`;

  const tests = [
    { key: "t1", name: own("test_1"), count: 20 },
    { key: "t2", name: own("test_2"), count: 15 },
  ];
  // Bank questions, as the builder lists them: status and difficulty above the name.
  const questions = [
    { key: "q1", name: ex("question_1"), level: "A" },
    { key: "q2", name: ex("question_2"), level: "B" },
    { key: "q3", name: ex("question_3"), level: "C" },
  ];
  const bankRow = (q, { check = false, mark } = {}) => (
    <MiniCard
      key={q.key}
      icon={<MiniCheck checked={check} />}
      aside={
        <MiniButton variant="link" icon={PlusIcon} mark={mark}>
          {roomy ? add : undefined}
        </MiniButton>
      }
    >
      <MiniRow>
        <MiniStatus code="APPROVED" />
        <MiniLevel level={q.level} />
      </MiniRow>
      <MiniText variant="strong" truncate>
        {q.name}
      </MiniText>
    </MiniCard>
  );
  // A question of the test: its handle, number and name, then its points and "⋯"; below 640px on a line under the
  // name, the points at its start and "⋯" at its end. "⋯" is framed below 768px, plain from there (OverflowMenu).
  const testRow = (number, q, points, marks = false) => {
    const name = (
      <MiniRow className="flex-nowrap">
        <MiniIcon icon={EllipsisVerticalIcon} />
        <MiniText variant="muted">{number}</MiniText>
        <MiniText variant="strong" truncate>
          {q.name}
        </MiniText>
      </MiniRow>
    );
    const stepper = <MiniStepper value={points} mark={marks ? "1" : undefined} />;
    const menu = <MiniButton variant={wide ? "quiet" : "outline"} icon={EllipsisHorizontalIcon} mark={marks ? "2" : undefined} />;
    return (
      <MiniCard key={q.key}>
        {roomy ? (
          <MiniRow className="justify-between flex-nowrap">
            {name}
            <MiniRow className="flex-nowrap">
              {stepper}
              {menu}
            </MiniRow>
          </MiniRow>
        ) : (
          <>
            {name}
            <MiniRow className="justify-between flex-nowrap">
              {stepper}
              {menu}
            </MiniRow>
          </>
        )}
      </MiniCard>
    );
  };

  return (
    <>
      <HelpIntro>{t("help.tests.intro")}</HelpIntro>

      <HelpFlow pipeline="exams" current="tests" />

      <HelpSteps>
        <HelpStep
          title={t("help.tests.steps.create.title")}
          illustration={
            <MiniScreen title={t("nav.test_bank")}>
              {wide ? (
                <>
                  <MiniRow className="justify-between gap-y-3">
                    <MiniText variant="title">{t("nav.test_bank")}</MiniText>
                    <MiniButton variant="primary" icon={PlusIcon} mark>
                      {create}
                    </MiniButton>
                  </MiniRow>
                  <MiniTable
                    columns={[
                      t("test_bank.list.columns.test"),
                      { label: t("test_bank.list.columns.status"), width: "6.5rem" },
                      { label: t("test_bank.list.columns.questions"), width: "5.5rem", align: "right" },
                      { label: "", width: "2.25rem" },
                    ]}
                    rows={tests.map((test) => [
                      <MiniText variant="strong" truncate>
                        {test.name}
                      </MiniText>,
                      <MiniBadge tone="green">{t("exam_bank.status.active")}</MiniBadge>,
                      String(test.count),
                      <MiniIcon icon={EllipsisHorizontalIcon} />,
                    ])}
                  />
                </>
              ) : (
                <>
                  <MiniText variant="title">{t("nav.test_bank")}</MiniText>
                  {/* Phones: the main button the screen's width; cards with the status by the name, "⋯" in the corner. */}
                  <FillButton icon={PlusIcon} mark>
                    {create}
                  </FillButton>
                  {tests.map((test) => (
                    <MiniCard key={test.key}>
                      <MiniRow className="justify-between flex-nowrap">
                        <MiniRow>
                          <MiniText variant="strong" truncate>
                            {test.name}
                          </MiniText>
                          <MiniBadge tone="green">{t("exam_bank.status.active")}</MiniBadge>
                        </MiniRow>
                        <MiniButton variant="outline" icon={EllipsisHorizontalIcon} />
                      </MiniRow>
                      <MiniText variant="muted" truncate>
                        {t("question_list.count", { count: test.count })}
                      </MiniText>
                    </MiniCard>
                  ))}
                </>
              )}
            </MiniScreen>
          }
        >
          <HelpText i18nKey="help.tests.steps.create.text" values={{ create }} />
          {/* The Folders page's other way: a subject's "⋯" makes a test of its approved questions at once. */}
          <p>
            <HelpText i18nKey="help.tests.steps.create.text_folder" values={{ folders: t("nav.folders"), make: t("folders_page.make_test") }} />
          </p>
        </HelpStep>

        <HelpStep
          title={t("help.tests.steps.settings.title")}
          illustration={
            <MiniScreen title={t("test_bank.form.create_new_test")}>
              <MiniRow>
                <MiniField label={t("test_bank.form.test_name")} value={own("test_1")} className="flex-1" />
              </MiniRow>
              {/* The page heading, empty: it may stay so. */}
              <MiniRow>
                <MiniField label={heading} className="flex-1" />
              </MiniRow>
              <MiniRow>
                <MiniCheck checked>{calculator}</MiniCheck>
              </MiniRow>
              <MiniRow>
                <MiniCheck>{table}</MiniCheck>
              </MiniRow>
              <MiniRow className="justify-between">
                <MiniText variant="strong">{t("test_bank.form.additional_information")}</MiniText>
                <MiniButton variant="quiet" icon={ArrowUpTrayIcon}>
                  {t("test_bank.form.upload_docx")}
                </MiniButton>
              </MiniRow>
              <MiniRow className="justify-end">
                <MiniButton variant="primary" mark>
                  {submit}
                </MiniButton>
              </MiniRow>
            </MiniScreen>
          }
        >
          <HelpText i18nKey="help.tests.steps.settings.text" values={{ calculator, table }} />
          <p>
            <HelpText i18nKey="help.tests.steps.settings.text_heading" values={{ heading }} />
          </p>
          <p>
            <HelpText i18nKey={wide ? "help.tests.steps.settings.text_next" : "help.tests.steps.settings.text_next_phone"} values={{ submit, edit }} />
          </p>
        </HelpStep>

        <HelpStep
          title={t("help.tests.steps.find.title")}
          illustration={
            <MiniScreen title={bankTab}>
              {!split && <MiniTabs fill items={[bankTab, testTab]} current={0} />}
              {/* The folder is one choice before the search (a row of its own on phones); from 768px the sorting stands
                  beside Filters, which opens its panel over the list; below, Filters folds the sorting and the filters
                  out under the search. */}
              {wide ? (
                <MiniRow className="gap-y-3">
                  <MiniField value={folderChoice} select mark="1" />
                  <MiniField icon={MagnifyingGlassIcon} placeholder={t("test_bank.editor.search_placeholder")} className="flex-1" />
                  <MiniField value={t("test_bank.builder.sort_newest")} select />
                  <MiniButton variant="outline" icon={AdjustmentsHorizontalIcon} mark="2">
                    {filters}
                  </MiniButton>
                </MiniRow>
              ) : (
                <>
                  <MiniRow>
                    <MiniField value={folderChoice} select mark="1" className="flex-1" />
                  </MiniRow>
                  <MiniRow className="flex-nowrap gap-y-3">
                    <MiniField icon={MagnifyingGlassIcon} placeholder={t("test_bank.editor.search_placeholder")} className="flex-1" />
                    <MiniButton variant="outline" icon={AdjustmentsHorizontalIcon} mark="2">
                      {filters}
                    </MiniButton>
                  </MiniRow>
                </>
              )}
              <MiniRow className="justify-between">
                <MiniCheck>{`${t("test_bank.builder.found")} 52`}</MiniCheck>
                <MiniCheck checked>{hide}</MiniCheck>
              </MiniRow>
            </MiniScreen>
          }
        >
          <HelpText
            i18nKey={wide ? "help.tests.steps.find.text" : "help.tests.steps.find.text_phone"}
            values={{ whole: t("test_bank.builder.folder_whole"), all: t("folders.all_questions"), filters, hide }}
          />
          {/* The builder lists approved questions only. */}
          <p>
            <HelpText i18nKey="help.tests.steps.find.text_approved" />
          </p>
          {/* Below 1280px the builder shows the bank and the test as two tabs. */}
          {!split && (
            <p>
              <HelpText i18nKey="help.tests.steps.find.text_tabs" values={{ bank: bankTab, test: testTab }} />
            </p>
          )}
        </HelpStep>

        <HelpStep
          title={t("help.tests.steps.add.title")}
          illustration={
            <MiniStack>
              <MiniCard tone="blue">
                <MiniRow className="justify-between gap-y-3">
                  <MiniText variant="strong">{t("test_bank.builder.selected_count", { count: 2 })}</MiniText>
                  <MiniRow className="gap-y-3">
                    <MiniButton variant="link">{t("test_bank.editor.clear_selection")}</MiniButton>
                    <MiniButton variant="primary" icon={PlusIcon} mark="2">
                      {addManyPicture}
                    </MiniButton>
                  </MiniRow>
                </MiniRow>
              </MiniCard>
              {bankRow(questions[0], { mark: "1" })}
              {bankRow(questions[1], { check: true })}
              {bankRow(questions[2], { check: true })}
            </MiniStack>
          }
        >
          <HelpText i18nKey={roomy ? "help.tests.steps.add.text" : "help.tests.steps.add.text_phone"} values={{ add, addToTest }} />
          <p>
            <HelpText i18nKey="help.tests.steps.add.text_many" values={{ addMany }} />
          </p>
        </HelpStep>

        <HelpStep
          title={t("help.tests.steps.arrange.title")}
          illustration={
            <MiniScreen title={testTab}>
              <MiniRow className="justify-between gap-y-3">
                <MiniText variant="muted">{defaultPoints}</MiniText>
                <MiniStepper value={1} />
              </MiniRow>
              <MiniDivider />
              {testRow(1, questions[0], 3)}
              {testRow(2, questions[1], 5, true)}
              <MiniRow className="justify-end">
                <MiniMenu
                  items={[
                    { label: t("test_bank.builder.view"), icon: EyeIcon },
                    { label: t("test_bank.builder.move_up"), icon: ArrowUpIcon },
                    { label: t("test_bank.builder.move_down"), icon: ArrowDownIcon },
                    { label: t("test_bank.builder.move_top"), icon: ChevronDoubleUpIcon },
                    { label: t("test_bank.builder.move_bottom"), icon: ChevronDoubleDownIcon },
                    { label: remove, icon: TrashIcon, danger: true },
                  ]}
                />
              </MiniRow>
            </MiniScreen>
          }
        >
          <HelpText i18nKey="help.tests.steps.arrange.text" values={{ remove }} />
          <p>
            <HelpText i18nKey="help.tests.steps.arrange.text_points" values={{ defaultPoints }} />
          </p>
        </HelpStep>

        <HelpStep
          title={t("help.tests.steps.finish.title")}
          illustration={
            <MiniScreen title={own("test_1")}>
              {wide ? (
                <MiniRow className="justify-between gap-y-3">
                  <MiniStack tight>
                    <MiniRow>
                      <MiniText variant="caps">{t("test_bank.builder.eyebrow")}</MiniText>
                    </MiniRow>
                    <MiniRow>
                      <MiniText variant="title">{own("test_1")}</MiniText>
                    </MiniRow>
                  </MiniStack>
                  <MiniRow className="gap-y-3">
                    <MiniButton variant="quiet" icon={PencilSquareIcon}>
                      {edit}
                    </MiniButton>
                    <MiniButton variant="quiet" icon={EyeIcon} mark="1">
                      {preview}
                    </MiniButton>
                    <MiniButton variant="primary" icon={CheckIcon} mark="2">
                      {done}
                    </MiniButton>
                  </MiniRow>
                </MiniRow>
              ) : (
                <>
                  {/* Below 640px Done fills its row beside "⋯"; up to 768px both stand beside the name. */}
                  {roomy ? (
                    <MiniRow className="justify-between gap-y-3">
                      <MiniText variant="title">{own("test_1")}</MiniText>
                      <MiniRow className="flex-nowrap">
                        <MiniButton variant="primary" icon={CheckIcon}>
                          {done}
                        </MiniButton>
                        <MiniButton variant="outline" icon={EllipsisHorizontalIcon} mark="1" />
                      </MiniRow>
                    </MiniRow>
                  ) : (
                    <>
                      <MiniText variant="title">{own("test_1")}</MiniText>
                      <MiniRow className="flex-nowrap">
                        <FillButton icon={CheckIcon}>{done}</FillButton>
                        <MiniButton variant="outline" icon={EllipsisHorizontalIcon} mark="1" />
                      </MiniRow>
                    </>
                  )}
                  <MiniRow className="justify-end">
                    <MiniMenu
                      items={[
                        { label: preview, icon: EyeIcon, mark: "2" },
                        { label: edit, icon: PencilSquareIcon },
                      ]}
                    />
                  </MiniRow>
                </>
              )}
              <MiniRow>
                <MiniIcon icon={CheckCircleIcon} tone="green" />
                <MiniText variant="muted">{saved}</MiniText>
              </MiniRow>
            </MiniScreen>
          }
        >
          <HelpText i18nKey="help.tests.steps.finish.text" values={{ saved, preview, done }} />
          {/* Phones keep Done and put Preview and Edit in "⋯" (the builder's header). */}
          {!wide && (
            <p>
              <HelpText i18nKey="help.tests.steps.finish.text_phone" values={{ preview, edit, done }} />
            </p>
          )}
        </HelpStep>

        <HelpStep
          title={t("help.tests.steps.exam.title")}
          illustration={
            <MiniStack>
              <MiniScreen title={t("nav.exam_bank")}>
                <MiniRow className="justify-between gap-y-3">
                  <MiniText variant="title">{ex("exam_2")}</MiniText>
                  <MiniButton variant="primary" icon={PlusIcon} mark="1">
                    {addTest}
                  </MiniButton>
                </MiniRow>
              </MiniScreen>
              <MiniDialog title={t("exam_bank.add_test.title")}>
                <MiniCard icon={DocumentPlusIcon} aside={<MiniIcon icon={ChevronRightIcon} />}>
                  <MiniText variant="strong" truncate>
                    {createNew}
                  </MiniText>
                  <MiniText variant="muted" truncate>
                    {t("exam_bank.add_test.create_new_hint")}
                  </MiniText>
                </MiniCard>
                <MiniCard icon={ListBulletIcon} aside={<MiniIcon icon={ChevronRightIcon} />} mark="2">
                  <MiniText variant="strong" truncate>
                    {existing}
                  </MiniText>
                  <MiniText variant="muted" truncate>
                    {t("exam_bank.add_test.select_existing_hint")}
                  </MiniText>
                </MiniCard>
              </MiniDialog>
            </MiniStack>
          }
        >
          <HelpText i18nKey="help.tests.steps.exam.text" values={{ exams: t("nav.exam_bank"), addTest, existing, createNew }} />
        </HelpStep>
      </HelpSteps>

      <HelpLegend
        rows={[
          { key: "in_test", label: t("test_bank.builder.in_test"), tone: "green", icon: CheckIcon, text: t("help.tests.legend.in_test") },
          // The test's rows show these two as an amber mark by the points (their label is the mark's tooltip).
          {
            key: "not_approved",
            badge: (
              <MiniRow>
                <MiniIcon icon={ExclamationTriangleIcon} tone="amber" />
                <MiniText>{t("test_bank.builder.not_approved")}</MiniText>
              </MiniRow>
            ),
            text: t("help.tests.legend.not_approved"),
          },
          { key: "blocked", badge: <MiniIcon icon={LockClosedIcon} tone="amber" />, text: t("test_bank.builder.blocked_row") },
          // A question whose answers the reader may not read in the bank: its row's quiet words under its title.
          {
            key: "answers_hidden",
            badge: (
              <MiniRow>
                <MiniIcon icon={EyeSlashIcon} />
                <MiniText variant="muted">{t("test_bank.builder.answers_hidden_row")}</MiniText>
              </MiniRow>
            ),
            text: t("help.tests.legend.answers_hidden"),
          },
        ]}
      />

      <HelpTips
        items={[
          passages && <HelpText key="context" i18nKey="help.tests.tips.context" values={{ context: t("passages.context.button") }} />,
          <HelpText key="undo" i18nKey="help.tests.tips.undo" values={{ undo: t("test_bank.builder.undo") }} />,
          <HelpText key="delete" i18nKey="help.tests.tips.delete" />,
        ]}
      />
    </>
  );
}
